import { useState } from 'react'
import { Link } from 'react-router-dom'

import {
  REPORT_DATASET_LABELS,
  REPORT_DATASETS,
  REPORT_FORMATS,
  type ReportDataset,
  type ReportExport,
  type ReportFormat,
} from '@/api/reports'
import { NotLivePage, useNotLivePage } from '@/app/notLive'
import { Card, DataTable, EmptyState, LoadingBlock, type Column } from '@/design'
import { usePermissions } from '@/lib/access'
import { formatDateKey } from '@/lib/filters'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import { ExportStateBadge } from './badges'
import { useReportsQuery, useRequestExport } from './hooks'
import './reports.css'

function columns(search: string): Column<ReportExport>[] {
  return [
    {
      key: 'report',
      header: 'Report',
      cell: (row) => (row.reportId ? <Link to={`/reports/${row.reportId}${search}`}>{row.reportName}</Link> : row.reportName),
    },
    { key: 'dataset', header: 'Dataset', cell: (row) => REPORT_DATASET_LABELS[row.dataset] },
    { key: 'format', header: 'Format', cell: (row) => row.format.toUpperCase() },
    { key: 'requested', header: 'Requested', sortValue: (row) => row.requestedAt, cell: (row) => formatDateKey(row.requestedAt, true) },
    { key: 'requestedBy', header: 'Requested by', cell: (row) => row.requestedBy },
    { key: 'state', header: 'State', cell: (row) => <ExportStateBadge state={row.state} /> },
    {
      key: 'download',
      header: 'Download',
      cell: (row) => (
        <span data-tone={row.state === 'expired' ? 'muted' : undefined}>
          {row.state === 'expired' ? 'Link expired' : row.downloadHint}
        </span>
      ),
    },
  ]
}

/** Reports > Exports: every export requested from a report, and a form to request a new one. */
export function ExportsPage() {
  const notLive = useNotLivePage('exports')
  const exports = useReportsQuery('exports', (client) => client.exports())
  const requestExport = useRequestExport()
  const search = useFilterSearch()
  const access = usePermissions()

  const [dataset, setDataset] = useState<ReportDataset>(REPORT_DATASETS[0])
  const [format, setFormat] = useState<ReportFormat>('csv')

  const rows = exports.data?.data ?? []

  if (notLive) return <NotLivePage />

  return (
    <div className="ci-stack">
      {access.can('exports.create') ? (
        <Card title="Request an export" subtitle="Queues a new export for a dataset, independent of any saved report.">
          <form
            className="rp-request-form"
            onSubmit={(event) => {
              event.preventDefault()
              requestExport.mutate({ reportId: null, dataset, format })
            }}
          >
            <label className="rp-field">
              Dataset
              <select value={dataset} onChange={(event) => setDataset(event.target.value as ReportDataset)}>
                {REPORT_DATASETS.map((value) => (
                  <option key={value} value={value}>
                    {REPORT_DATASET_LABELS[value]}
                  </option>
                ))}
              </select>
            </label>
            <label className="rp-field">
              Format
              <select value={format} onChange={(event) => setFormat(event.target.value as ReportFormat)}>
                {REPORT_FORMATS.map((value) => (
                  <option key={value} value={value}>
                    {value.toUpperCase()}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit" className="ci-button" data-variant="primary" disabled={requestExport.isPending}>
              Request export
            </button>
          </form>
        </Card>
      ) : null}

      <Card title="Exports" subtitle="Every export requested for this organisation." busy={exports.isFetching}>
        {exports.isPending ? <LoadingBlock rows={6} /> : null}
        {exports.isError ? <EmptyState variant="outage" compact description="Exports could not be loaded." /> : null}
        {exports.data ? (
          <DataTable
            caption="Exports"
            columns={columns(search)}
            rows={rows}
            rowKey={(row) => row.id}
            initialSort={{ key: 'requested', direction: 'desc' }}
            empty={<EmptyState variant="no-data" compact title="No exports yet" description="Requested exports will appear here." />}
          />
        ) : null}
      </Card>
    </div>
  )
}
